import { AxiosResponse } from "axios";
import Local from "../../common/classes/local";
import { mockGovDeliveryUrlFailParams } from "../../common/functions/govDeliveryErrors";
import useAxios from "../../common/hooks/useAxios";

export type SecurityCodeMethod = 'sms' | 'voice' | 'email';
interface Step1Body {
    type: SecurityCodeMethod
    value: string
}
export interface ConfiguredMfaOption {
    id: string
    type: SecurityCodeMethod
    mfa_channel: string
}
export interface MfaEnrollStep1Response {
    status: 'error' | 'created'
    code: number
    errors?: Step1ErrorResponse[] | null
}
export interface MfaEnrollStep2Response {
    code: number
    configured_option: ConfiguredMfaOption
    errors?: Step2ErrorResponse[] | null
    status: 'created' | 'error'
}

export interface MfaEnrollGetEmailResponse {
    status: 'ok' | 'error'
    code: 200 | 401 | 500
    errors?: MfaEnrollGetEmailResponseError[]
    email?: string
}

interface MfaEnrollGetEmailResponseError {
    type: 'Unauthorized' | 'ServerError'
    message: 'UPDATE_ACCOUNT_UNAUTHORIZED' | 'MFA_SERVER_ERROR'
    name: 'Unauthorized' | 'ServerError'
}
export interface MfaDeleteErrorResponse {
    status: 'error'
    type: Step2ErrorName
    message: 'UPDATE_ACCOUNT_UNAUTHORIZED' | 'MFA_SERVER_ERROR'
    code?: number
    errors?: DeleteError[] | null
}

type DeleteError = 'Unauthorized' | 'Conflict' | 'ServerError';
interface Step1ErrorResponse  {
    type: Step1ErrorName
    message: 'INVALID_MFA_VALUE' | 'GOV_DELIVERY_ERROR' | 'UPDATE_ACCOUNT_UNAUTHORIZED' | 'MFA_DUPLICATE_CHANNEL' | 'MFA_CHANNEL_EXCEEDS_USE_LIMIT' | 'MFA_SERVER_ERROR' | 'MFA_NON_US_PHONE_NUMBER' | 'MFA_GOVDELIVERY_UNAVAILABLE'
    name: Step1ErrorName
}

type Step1ErrorName = 'ValidationError' | 'GovDeliveryError' | 'Unauthorized' | 'Conflict' | 'ServerError' | 'BadRequest' | 'InternalError';
interface Step2ErrorResponse  {
    type: Step2ErrorName
    message: 'INVALID_MFA_VALUE' | 'GOV_DELIVERY_ERROR' | 'UPDATE_ACCOUNT_UNAUTHORIZED' | 'MFA_SERVER_ERROR' | "REDIS_ERROR" | "MFA_EXPIRED_OTP | 'MFA_GOVDELIVERY_UNAVAILABLE"
    name: Step2ErrorName
}

export type MfaResponse<T> = T extends MfaEnrollStep1Response ? MfaEnrollStep1Response : MfaEnrollStep2Response;

type Step2ErrorName = 'ValidationError' | 'GovDeliveryError' | 'Unauthorized' | 'Conflict' | 'ServerError' | 'BadRequest'  | 'InternalError';

export interface GetOptionsResponse {
    status: 'ok' | 'error'
    code: 200 | 401 | 500
    configured_options: ConfiguredMfaOption[]
    error?: OptionsErrors[]
}
export interface OptionsErrors {
    type: 'Unauthorized' | 'ServerError'
    message: 'UPDATE_ACCOUNT_UNAUTHORIZED' | 'SERVER_ERROR',
    name: 'Unauthorized' | 'ServerError'
}

export const useMfaApi = () => {
    const axios = useAxios();

    const mfaEnrollStep1 = ({ type, value }: Step1Body) => {
        const lang = Local.getItem('language') === 'Spanish' ? 'es' : 'en';
        const params = mockGovDeliveryUrlFailParams();
        const url = `/account/api/mfa/enroll/step/1${params}`;
        return axios.call<MfaEnrollStep1Response>(
            "POST",
            url,
            {
                type: type,
                mfa_channel: value,
                lang,
            }
        )
    }

    const mfaEnrollStep2 = (value: string) => {
        return axios.call<MfaEnrollStep2Response>(
            "POST",
            "/account/api/mfa/enroll/step/2",
            {
                otp: value
            }
        )
    }

    const mfaEnrollGetEmail = () => {
        return axios.call<MfaEnrollGetEmailResponse>(
            "GET",
            "/account/api/mfa/enroll/email"
        )
    }

    const mfaUpdateCookie = ({ password }: { password: string }) => {
        return axios.call(
            "POST",
            "/account/api/update-account-cookie",
            { password },
        )
    }

    const mfaVerifyCookie = () => {
        return axios.call(
            "GET",
            "/account/api/update-account-cookie"
        )
    }

    const mfaGetOptions = async (): Promise<AxiosResponse<GetOptionsResponse> | null> => {
        return axios.call(
            "GET",
            "/account/api/mfa"
        )
    }

    const mfaDelete = (uuid: string) => {
        return axios.call(
            "POST",
            `/account/api/mfa/remove/${uuid}`
        )
    }

    return {
        mfaEnrollStep1,
        mfaEnrollStep2,
        mfaUpdateCookie,
        mfaGetOptions,
        mfaVerifyCookie,
        mfaDelete,
        mfaEnrollGetEmail
    }
}